import { getAccessContext } from '../services/accessContext'
import {
  canAssignTenantRoles,
  canCreateProviderAccount,
  canEditTenantRoles,
  canManageInitialOwnerInvitation,
  canManageProtectedTenantAdminRoles,
  canOpenProviderAccessManagement,
  canUpdateProviderContract,
  canUpdateProviderFeatures,
  isProviderReadOnly
} from './accessPolicy'

const STATUS_LABELS = {
  ACTIVE: '利用中',
  TRIAL: '無料試用中',
  PREPARING: '利用開始前',
  SUSPENDED: '利用停止中',
  CANCELLED: '解約済み',
  EXPIRED: '契約終了'
}

const PROTECTED_ROLE_NAMES = [
  '契約企業最高管理者',
  '契約企業管理者（自社最上位）',
  '社内システム管理者'
]

const PROVIDER_MUTATION_BUTTONS = {
  create: ['契約企業を追加', '作成して招待メールを送信'],
  contract: ['契約を更新'],
  feature: ['利用可能', '利用不可'],
  invitation: ['招待メールを再送', '招待先を変更', '招待を取消', '変更して招待を再送']
}

function replaceExactText (root, selector, replacements) {
  root.querySelectorAll(selector).forEach(element => {
    const current = String(element.textContent || '').trim()
    if (Object.prototype.hasOwnProperty.call(replacements, current)) {
      element.textContent = replacements[current]
    }
  })
}

function setDisabled (element, disabled, reason) {
  if (!element) return
  element.disabled = Boolean(disabled)
  element.setAttribute('aria-disabled', String(Boolean(disabled)))
  if (disabled && reason) element.setAttribute('title', reason)
  else if (!disabled) element.removeAttribute('title')
}

function normalizedLabel (element) {
  return String(element && element.textContent ? element.textContent : '')
    .replace(/\s+/g, ' ')
    .trim()
}

function includesLabel (label, candidates) {
  return candidates.some(candidate => label === candidate || label.includes(candidate))
}

function disableWithin (root, reason) {
  if (!root) return
  root.querySelectorAll('button, input, select, textarea').forEach(element => {
    setDisabled(element, true, reason)
  })
}

function normalizeRoleTerminology (root) {
  replaceExactText(root, '*', {
    '契約企業最高管理者': '契約企業管理者（自社最上位）',
    '最高管理者は変更不可': '契約企業管理者は変更不可',
    '最高管理者は安全のため変更できません。': '契約企業管理者は安全のため変更できません。'
  })
}

function providerMode (context) {
  const roles = context && Array.isArray(context.roles) ? context.roles : []
  if (roles.includes('PLATFORM_SUPER_ADMIN')) return 'emergency'
  if (roles.includes('PLATFORM_OPERATIONS_ADMIN')) return 'operations'
  if (roles.includes('PLATFORM_SUPPORT_VIEWER')) return 'support'
  return 'none'
}

function ensureProviderNotice (page, context) {
  let notice = page.querySelector('[data-provider-access-notice]')
  if (!notice) {
    notice = document.createElement('div')
    notice.setAttribute('data-provider-access-notice', 'true')
    notice.setAttribute('role', 'status')
    notice.style.marginBottom = '16px'
    notice.style.padding = '12px 14px'
    notice.style.border = '1px solid #cbdbe5'
    notice.style.borderRadius = '10px'
    notice.style.lineHeight = '1.7'
    const header = page.querySelector('.page-header') || page.firstElementChild
    if (header && header.parentNode) header.parentNode.insertBefore(notice, header.nextSibling)
    else page.insertBefore(notice, page.firstChild)
  }

  const mode = providerMode(context)
  if (mode === 'support') {
    notice.style.background = '#f5f8fa'
    notice.style.color = '#405a69'
    notice.textContent = 'PREIKAサポート閲覧者として表示しています。契約内容・利用可能機能・招待状態・監査情報は確認できますが、作成・更新・招待送信はできません。'
  } else if (mode === 'operations') {
    notice.style.background = '#eef8ff'
    notice.style.color = '#185f8c'
    notice.textContent = 'PREIKA運営管理者として、付与された運営権限の範囲で、契約企業の開通・契約内容・利用可能機能・最初の管理者招待を操作できます。運営側の役割そのものは変更できません。'
  } else if (mode === 'emergency') {
    notice.style.background = '#fff8e8'
    notice.style.color = '#76530c'
    notice.textContent = 'PREIKA緊急最高管理者でログインしています。通常の契約開通・保守はPREIKA運営管理者を使用し、このロールは復旧時だけ利用してください。'
  } else {
    notice.style.background = '#fff0f0'
    notice.style.color = '#8c2933'
    notice.textContent = 'PREIKA運営Roleを確認できないため、契約企業管理の操作を停止しています。'
  }
}

function applyProviderControls (page, context) {
  const canOpen = canOpenProviderAccessManagement(context)
  const canCreate = canCreateProviderAccount(context)
  const canUpdateContract = canUpdateProviderContract(context)
  const canUpdateFeatures = canUpdateProviderFeatures(context)
  const canManageInvitation = canManageInitialOwnerInvitation(context)
  const readOnly = isProviderReadOnly(context)

  ensureProviderNotice(page, context)

  if (!canOpen) {
    disableWithin(page, 'PREIKA運営管理画面を利用できません。')
    return
  }

  page.querySelectorAll('button').forEach(button => {
    const label = normalizedLabel(button)
    if (includesLabel(label, PROVIDER_MUTATION_BUTTONS.create)) {
      setDisabled(button, !canCreate, '契約企業の作成にはACCOUNT_CREATEが必要です。')
    } else if (includesLabel(label, PROVIDER_MUTATION_BUTTONS.contract)) {
      setDisabled(button, !canUpdateContract, '契約変更にはCONTRACT_UPDATEが必要です。')
    } else if (includesLabel(label, PROVIDER_MUTATION_BUTTONS.invitation)) {
      setDisabled(button, !canManageInvitation, '初期管理者招待の操作にはACCOUNT_UPDATEが必要です。')
    }
  })

  if (!canUpdateFeatures) {
    page.querySelectorAll('input[type="checkbox"], .custom-switch input, [role="switch"]').forEach(element => {
      setDisabled(element, true, '企業別Featureの変更にはFEATURE_MANAGEが必要です。')
    })
  }

  const contractButton = Array.from(page.querySelectorAll('button'))
    .find(button => includesLabel(normalizedLabel(button), PROVIDER_MUTATION_BUTTONS.contract))
  if (contractButton && !canUpdateContract) {
    const contractPanel = contractButton.closest('.tab-pane, .card-body, form')
    if (contractPanel) {
      contractPanel.querySelectorAll('input, select, textarea').forEach(element => {
        setDisabled(element, true, '契約変更にはCONTRACT_UPDATEが必要です。')
      })
    }
  }

  const createButton = Array.from(page.querySelectorAll('button'))
    .find(button => includesLabel(normalizedLabel(button), ['作成して招待メールを送信']))
  if (createButton && !canCreate) {
    disableWithin(
      createButton.closest('.modal-content, form'),
      '契約企業の作成にはACCOUNT_CREATEが必要です。'
    )
  }

  if (readOnly) {
    page.querySelectorAll('.modal-footer button, .primary-action').forEach(button => {
      const label = normalizedLabel(button)
      if (!['キャンセル', '閉じる', '再読込'].includes(label)) {
        setDisabled(button, true, 'PREIKAサポート閲覧者は更新操作を行えません。')
      }
    })
  }
}

function normalizeContractManagementPage () {
  const heading = Array.from(document.querySelectorAll('h1')).find(element => String(element.textContent || '').trim() === '契約企業管理')
  if (!heading) return
  const page = heading.closest('.platform-access-page') || document

  replaceExactText(page, 'p', {
    '契約企業、契約プラン、利用上限、FeatureをAKI運営側で管理します。': 'PREIKA運営管理者が、契約企業の開通、契約プラン、利用人数上限、利用可能機能を管理します。契約企業側の自社権限管理とは別の運営画面です。',
    '契約企業、契約プラン、利用人数上限、利用可能機能をAKI運営側で管理します。': 'PREIKA運営管理者が、契約企業の開通、契約プラン、利用人数上限、利用可能機能を管理します。契約企業側の自社権限管理とは別の運営画面です。'
  })
  replaceExactText(page, 'th', {
    状態: '契約状態',
    上限: '利用人数上限',
    Feature: '機能',
    名称: '機能名',
    適用元: '設定元',
    有効: '利用可否'
  })
  replaceExactText(page, '.nav-link, [role="tab"]', {
    Feature: '利用可能機能'
  })
  replaceExactText(page, 'label', {
    利用者上限: '利用人数上限',
    初期管理者名: '最初の契約企業管理者名',
    初期管理者メール: '最初の契約企業管理者メール'
  })

  page.querySelectorAll('td').forEach(cell => {
    const value = String(cell.textContent || '').trim()
    if (Object.prototype.hasOwnProperty.call(STATUS_LABELS, value)) {
      cell.textContent = STATUS_LABELS[value]
    }
  })

  const headers = Array.from(page.querySelectorAll('th')).map(header => String(header.textContent || '').trim())
  const limitIndex = headers.indexOf('利用人数上限')
  if (limitIndex >= 0) {
    page.querySelectorAll('tbody tr').forEach(row => {
      const cell = row.children[limitIndex]
      if (!cell) return
      const value = String(cell.textContent || '').trim()
      if (!value || value === 'null') cell.textContent = '無制限'
      else if (/^\d+$/.test(value)) cell.textContent = `${value}名`
    })
  }

  replaceExactText(page, 'td', {
    PLAN: '契約プラン設定',
    OVERRIDE: '個別設定'
  })

  getAccessContext()
    .then(context => applyProviderControls(page, context))
    .catch(() => disableWithin(
      page,
      'PREIKA運営Role・Permissionを確認できないため操作を停止しています。'
    ))
}

function applyTenantManagerControls (page, context) {
  const editable = canEditTenantRoles(context)
  const assignable = canAssignTenantRoles(context)
  const protectedRoleManager = canManageProtectedTenantAdminRoles(context)

  page.querySelectorAll('button').forEach(button => {
    const label = String(button.textContent || '').trim()
    if (label === '役割を追加' || label === '自社用に変更' || label === '利用機能を編集') {
      setDisabled(
        button,
        !editable,
        '役割の利用機能を変更するには、社内システム管理者のROLE_MANAGEとPERMISSION_VIEWが必要です。'
      )
    }
    if (label === '追加' || label === '解除') {
      setDisabled(
        button,
        !assignable,
        'スタッフへ役割を設定するにはROLE_ASSIGNが必要です。'
      )
    }
  })

  if (!assignable) {
    page.querySelectorAll('select').forEach(select => setDisabled(
      select,
      true,
      'スタッフへ役割を設定するPermissionがありません。'
    ))
  }

  if (!protectedRoleManager) {
    page.querySelectorAll('select option').forEach(option => {
      const text = String(option.textContent || '').trim()
      if (PROTECTED_ROLE_NAMES.some(name => text.includes(name))) {
        option.disabled = true
        option.setAttribute('aria-disabled', 'true')
      }
    })

    page.querySelectorAll('tr, .mobile-record-card').forEach(row => {
      const text = String(row.textContent || '')
      if (!PROTECTED_ROLE_NAMES.some(name => text.includes(name))) return
      row.querySelectorAll('button').forEach(button => {
        const label = String(button.textContent || '').trim()
        if (label === '自社用に変更' || label === '利用機能を編集') {
          setDisabled(
            button,
            true,
            '契約企業管理者と社内システム管理者自身の変更は、契約企業管理者だけが行えます。'
          )
        }
      })
    })
  }
}

function normalizeTenantAccessManagementPage () {
  const heading = Array.from(document.querySelectorAll('h1')).find(element => String(element.textContent || '').trim() === '自社権限管理')
  if (!heading) return
  const page = heading.closest('.tenant-access-page') || document

  normalizeRoleTerminology(page)
  replaceExactText(page, 'p', {
    '自社スタッフに割り当てる役割と、各役割で利用できる機能を管理します。': '契約プランで利用可能な範囲内で、自社スタッフの役割と利用機能を管理します。契約企業管理者（自社最上位）または社内システム管理者だけが操作できます。PREIKA運営管理者の権限はこの画面では扱いません。権限と左メニューの対応は、右下の「ヘルプ」で確認できます。'
  })

  getAccessContext()
    .then(context => applyTenantManagerControls(page, context))
    .catch(() => {
      page.querySelectorAll('button, select').forEach(element => setDisabled(
        element,
        true,
        '契約・役割情報を確認できないため操作を停止しています。'
      ))
    })
}

function applyPolicy () {
  normalizeContractManagementPage()
  normalizeTenantAccessManagementPage()
}

export function startAccessManagementUiPolicy (router) {
  let scheduled = false
  const schedule = () => {
    if (scheduled) return
    scheduled = true
    window.requestAnimationFrame(() => {
      scheduled = false
      applyPolicy()
    })
  }

  const observer = new MutationObserver(schedule)
  observer.observe(document.body, { childList: true, subtree: true })
  if (router && typeof router.afterEach === 'function') router.afterEach(schedule)
  schedule()
  return () => observer.disconnect()
}
